<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { CaldavAccount } from '@/api'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { useAccountsEditor } from '@/composables/useAccountsEditor'
import { accountsPath } from '@/settings/accountsNav'

// The calendars of an account as a list like the todo lists: the big circle in front says whether the calendar is offered
// (subscribed), the rest of the row opens its settings (default, priority tags, sharing).
const props = defineProps<{ account: CaldavAccount }>()
const { t } = useI18n()
const editor = useAccountsEditor()
</script>

<template>
  <div class="account-calendars">
    <p v-if="editor.calendarsError[account.id]" class="error">{{ editor.calendarsError[account.id] }}</p>
    <p v-else-if="editor.calendarsLoading[account.id] && !editor.calendarsByAccount[account.id]">{{ t('common.loading') }}</p>
    <template v-else>
      <UiGroup v-if="(editor.calendarsByAccount[account.id] ?? []).length">
        <UiList>
          <UiRow
            v-for="calendar in editor.calendarsByAccount[account.id]"
            :key="calendar.id"
            :to="accountsPath(account.id, 'calendars', calendar.id)"
            class="entry"
            :data-calendar-id="calendar.id"
          >
            <button
              type="button"
              role="checkbox"
              class="ui-checkbox big"
              :aria-checked="calendar.subscribed"
              :aria-label="t('accounts.offerCalendar', { name: calendar.name })"
              @click.stop.prevent="editor.toggleSubscribed(props.account, calendar)"
            >
              <span v-if="calendar.subscribed">✓</span>
            </button>
            <span class="name">{{ calendar.name }}</span>
            <span v-if="calendar.is_default" class="ui-hint" :title="t('accounts.default')">⭐</span>
            <span class="go" aria-hidden="true">›</span>
          </UiRow>
        </UiList>
      </UiGroup>
      <p v-else class="ui-hint">{{ t('accounts.noCalendars') }}</p>

      <UiGroup v-if="(editor.calendarsByAccount[account.id] ?? []).length">
        <UiList>
          <UiRow as="button" type="button" class="action" @click="editor.setAllSubscribed(props.account, true)">
            {{ t('common.selectAll') }}
          </UiRow>
          <UiRow as="button" type="button" class="action" @click="editor.setAllSubscribed(props.account, false)">
            {{ t('common.selectNone') }}
          </UiRow>
        </UiList>
      </UiGroup>

      <UiGroup>
        <UiList>
          <UiRow
            as="button"
            type="button"
            class="action"
            :disabled="editor.calendarsLoading[account.id]"
            data-testid="refresh-calendars"
            @click="editor.loadCalendars(props.account, true)"
          >
            {{ editor.calendarsLoading[account.id] ? t('accounts.refreshing') : t('accounts.refresh') }}
          </UiRow>
        </UiList>
      </UiGroup>
    </template>
  </div>
</template>

<style scoped>
.entry {
  color: var(--tg-text-color);
  text-decoration: none;
}

.big {
  width: 1.75rem;
  height: 1.75rem;
  font-size: 1rem;
}

.name {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.go {
  flex: none;
  color: var(--tg-hint-color);
  font-size: 1.2rem;
}

.action {
  width: 100%;
  border: 0;
  background: none;
  color: var(--tg-link-color);
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.action:disabled {
  opacity: 0.6;
  cursor: default;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
